import { AutocompleteApi, InternalAutocompleteSource } from '@algolia/autocomplete-core';
import { css } from '@compiled/react';
import { useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import GenerativeIndicatorIcon from '@atlaskit/icon-lab/core/generative-indicator';
import { token } from '@atlaskit/tokens';

import { wacToken } from '@/libraries/design/foundations';

import { ContentHit } from './auto-complete-search-bar';

type AutoCompleteSearchAIResultProps = {
	query: string;
	autocomplete: AutocompleteApi<
		ContentHit,
		// eslint-disable-next-line  @typescript-eslint/no-explicit-any
		React.BaseSyntheticEvent<object, any, any>,
		React.MouseEvent<Element, MouseEvent>,
		React.KeyboardEvent<Element>
	>;
	source: InternalAutocompleteSource<ContentHit>;
	item: ContentHit;
};

const aiResultContainerStyle = css({
	display: 'flex',
	justifyContent: 'space-between',
	alignItems: 'center',
	borderRadius: '4px',
	minHeight: 40,
	padding: '4px 8px',
	marginBlockEnd: '8px !important',
	backgroundColor: wacToken('color.extended.blue100'),
	cursor: 'pointer',

	'&:first-child': {
		marginBlockStart: '0px',
	},

	'&:last-child': {
		marginBlockEnd: '0px',
	},

	'&[aria-selected=true]': {
		visibility: 'visible',
		backgroundColor: wacToken('color.neutral.light200'),
	},
});

const aiIconSearchWrapperStyle = css({
	display: 'flex',
	justifyContent: 'flex-start',
	alignItems: 'center',
	gap: '10px',
});

const queryStyle = css({
	fontWeight: 600,
});

const aiSearchTextStyle = css({
	color: wacToken('color.neutral.light600'),
	fontSize: '12px',
});

const AutoCompleteSearchAIResult = ({
	query,
	autocomplete,
	source,
	item,
}: AutoCompleteSearchAIResultProps) => {
	const intl = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const href = `/search?q=${query}`;

	const aiSearchText = intl.formatMessage({
		id: 'ai.search.text',
		defaultMessage: 'AI search',
	});

	return (
		<li className="aa-Item" {...autocomplete.getItemProps({ item, source })}>
			<a
				css={aiResultContainerStyle}
				href={href}
				onClick={(event) => {
					event.preventDefault();
					createAnalyticsEvent({
						eventName: 'clickAutocompleteSearchResult',
						eventType: 'ui',
						action: 'clicked',
						actionSubject: 'linkButton',
						actionSubjectId: 'globalNavSearchResults',
						source: 'globalNav',
						attributes: {
							product: 'search',
							queryId: item.queryID,
							objectId: item.objectID,
							linkUrl: href,
							pageUrl: window.location.href,
							pagePath: window.location.pathname,
							rank: 0,
						},
					}).fire();
					window.location.href = href;
				}}
			>
				<div css={aiIconSearchWrapperStyle}>
					<GenerativeIndicatorIcon color={token('color.icon.brand')} label="" size="medium" />
					<div css={queryStyle}>{query}</div>
				</div>
				<span css={aiSearchTextStyle}>{aiSearchText}</span>
			</a>
		</li>
	);
};

export default AutoCompleteSearchAIResult;
